/* eslint-disable camelcase */
/**
 * Zod schemas at the consumer boundary — Phase 2 Stream M.
 *
 * Why this file exists:
 *   The orchestrator emits its card payloads twice — once on the SSE
 *   stream (snake_case, untouched) and once via the replay endpoint
 *   (camelCase, because the global axios interceptor recursively
 *   camelizes every JSON response, INCLUDING the deep JSONField
 *   contents). Two consumers, two key spellings, one runtime —
 *   exactly the failure mode the camelCase memory write-up flagged.
 *
 *   Phase 1 patched this by sprinkling `humps.decamelizeKeys` at
 *   each consumer (`AssistantMessageBody`). That works but scatters
 *   the boundary; a future endpoint that emits cards lands the same
 *   bug. Phase 2 Stream M centralizes the boundary HERE: one Zod
 *   schema per `card_kind` that accepts BOTH spellings and emits the
 *   canonical snake_case shape that the variant renderers already
 *   consume.
 *
 * DRY contract: these schemas match the Pydantic shapes in
 *   `backend/server/chat/tools/schema.json` 1:1. Adding a new field
 *   means editing TWO places — the Pydantic class on the BE and the
 *   schema below. The cross-surface contract test in
 *   `__tests__/schemas.test.ts` asserts every `card_kind` from the
 *   typescript union has a schema below.
 *
 * Performance:
 *   Zod's `.parse()` walks the object once per card payload. Each
 *   turn carries at most a handful of cards (typically 1-3), and the
 *   schemas are flat. The cost is negligible compared to the React
 *   reconciliation that follows.
 *
 * Failure mode:
 *   `parseToolCard` returns `null` on a payload that doesn't match
 *   any known `card_kind`. The dispatcher logs + skips. This is a
 *   strict upgrade over the Phase 1 behavior where a malformed
 *   payload would crash the whole turn with "Objects are not valid
 *   as a React child."
 */
import { z } from "zod";

import { dualKey, isObject } from "@/api/codec";
import { asPercentClamped, type Percent } from "@/types/nominal";

import type {
  BandedFloat,
  CardKind,
  CitationRef,
  CodeApplicabilityExplainComputed,
  CodeApplicabilityExplainLookup,
  CodeApplicabilityExplainVisual,
  FilingStatus,
  ListActiveViolations,
  ListBuildingsEligibleForCode,
  ListPendingOwnerQuestions,
  MitigationLever,
  MitigationLeverRow,
  PendingClassification,
  PortfolioComplianceSummary,
  PortfolioPenaltyTotalBreakdown,
  SimulateRetrofit,
  SimulateRetrofitRow,
  SourceCitation,
  StartFilingFor,
  ToolCardPayload,
} from "./shared/types";

// ``dualKey`` + ``isObject`` (wire-shape tolerance) are imported from
// ``@/api/codec`` — the single shared implementation of the snake/camel
// idiom. ``dualKey(obj, "snake_case")`` resolves the snake form first
// (canonical BE convention), camelized spelling as fall-back, so these
// schemas parse identically whether the bytes came from the axios-raw
// path or the SSE path.

// ---------------------------------------------------------------------
// Primitive sub-schemas
// ---------------------------------------------------------------------

const citationRefSchema = z.unknown().transform((raw, ctx): CitationRef => {
  if (!isObject(raw)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "expected object" });
    return z.NEVER;
  }
  const label = dualKey(raw, "label");
  if (typeof label !== "string") {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "citation.label must be a string",
    });
    return z.NEVER;
  }
  const href = dualKey(raw, "href");
  return {
    label,
    href: typeof href === "string" ? href : null,
  };
});

const nullableCitationRef = z
  .unknown()
  .transform((raw, ctx): CitationRef | null => {
    if (raw === undefined || raw === null) return null;
    const parsed = citationRefSchema.safeParse(raw);
    if (!parsed.success) {
      parsed.error.issues.forEach((i) => ctx.addIssue(i));
      return z.NEVER;
    }
    return parsed.data;
  });

// ---------------------------------------------------------------------
// Percent — the brand we own at this boundary.
//
// We clamp rather than reject because real BE outputs occasionally
// land at 100.0000001 (floating-point sums). The 0–100 contract is
// the source of truth — a value at 99.9 is a percent, full stop.
// ---------------------------------------------------------------------

const percentSchema = z.number().transform((n): Percent => asPercentClamped(n));

// ---------------------------------------------------------------------
// Helpers — read base-card fields tolerant of both spellings.
// ---------------------------------------------------------------------

interface BaseFields {
  readonly source: string;
  readonly as_of?: string | null;
  readonly classification_version?: string | null;
  readonly expandable?: boolean;
}

const readBaseFields = (raw: Record<string, unknown>): BaseFields => {
  const source = dualKey(raw, "source");
  const as_of = dualKey(raw, "as_of");
  const classification_version = dualKey(raw, "classification_version");
  const expandable = dualKey(raw, "expandable");
  return {
    source: typeof source === "string" ? source : "",
    as_of: typeof as_of === "string" ? as_of : null,
    classification_version:
      typeof classification_version === "string"
        ? classification_version
        : null,
    expandable: typeof expandable === "boolean" ? expandable : undefined,
  };
};

// ---------------------------------------------------------------------
// Card variants
// ---------------------------------------------------------------------

const buildingEligibilityRowSchema = z.unknown().transform((raw, ctx) => {
  if (!isObject(raw)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "expected object" });
    return z.NEVER;
  }
  const building_name = dualKey(raw, "building_name");
  const reason = dualKey(raw, "reason");
  if (typeof building_name !== "string" || typeof reason !== "string") {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "building_name + reason required",
    });
    return z.NEVER;
  }
  const bbl = dualKey(raw, "bbl");
  const bin = dualKey(raw, "bin");
  const why = dualKey(raw, "why");
  const whyParsed = nullableCitationRef.safeParse(why);
  return {
    building_name,
    reason,
    bbl: typeof bbl === "string" ? bbl : null,
    bin: typeof bin === "string" ? bin : null,
    why: whyParsed.success ? whyParsed.data : null,
  };
});

const listBuildingsSchema = z
  .unknown()
  .transform((raw, ctx): ListBuildingsEligibleForCode => {
    if (!isObject(raw)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "expected object" });
      return z.NEVER;
    }
    const base = readBaseFields(raw);
    const code = dualKey(raw, "code");
    const subject_count = dualKey(raw, "subject_count");
    const portfolio_count = dualKey(raw, "portfolio_count");
    const headline = dualKey(raw, "headline");
    const rowsRaw = dualKey(raw, "rows");
    const rows = Array.isArray(rowsRaw)
      ? rowsRaw
          .map((r) => buildingEligibilityRowSchema.safeParse(r))
          .filter(
            (
              p,
            ): p is {
              success: true;
              data: ReturnType<typeof buildingEligibilityRowSchema.parse>;
            } => p.success,
          )
          .map((p) => p.data)
      : [];
    return {
      card_kind: "list_buildings_eligible_for_code",
      ...base,
      code: typeof code === "string" ? code : "",
      subject_count: typeof subject_count === "number" ? subject_count : 0,
      portfolio_count:
        typeof portfolio_count === "number" ? portfolio_count : 0,
      headline: typeof headline === "string" ? headline : "",
      rows,
    };
  });

const portfolioKpiSchema = z.unknown().transform((raw, ctx) => {
  if (!isObject(raw)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "expected object" });
    return z.NEVER;
  }
  const label = dualKey(raw, "label");
  const value = dualKey(raw, "value");
  const detail = dualKey(raw, "detail");
  return {
    label: typeof label === "string" ? label : "",
    value: typeof value === "string" ? value : "",
    detail: typeof detail === "string" ? detail : null,
  };
});

const portfolioSummarySchema = z
  .unknown()
  .transform((raw, ctx): PortfolioComplianceSummary => {
    if (!isObject(raw)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "expected object" });
      return z.NEVER;
    }
    const base = readBaseFields(raw);
    const headline = dualKey(raw, "headline");
    const kpisRaw = dualKey(raw, "kpis");
    const kpis = Array.isArray(kpisRaw)
      ? kpisRaw
          .map((k) => portfolioKpiSchema.safeParse(k))
          .filter((p) => p.success)
          .map((p) => p.data)
      : [];
    return {
      card_kind: "portfolio_compliance_summary",
      ...base,
      headline: typeof headline === "string" ? headline : "",
      kpis,
    };
  });

const fieldRowSchema = z.unknown().transform((raw, ctx) => {
  if (!isObject(raw)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "expected object" });
    return z.NEVER;
  }
  const field_name = dualKey(raw, "field_name");
  const value = dualKey(raw, "value");
  const why = dualKey(raw, "why");
  const whyParsed = nullableCitationRef.safeParse(why);
  return {
    field_name: typeof field_name === "string" ? field_name : "",
    value: typeof value === "string" ? value : "",
    why: whyParsed.success ? whyParsed.data : null,
  };
});

const lookupSchema = z
  .unknown()
  .transform((raw, ctx): CodeApplicabilityExplainLookup => {
    if (!isObject(raw)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "expected object" });
      return z.NEVER;
    }
    const base = readBaseFields(raw);
    const code = dualKey(raw, "code");
    const building_label = dualKey(raw, "building_label");
    const summary = dualKey(raw, "summary");
    const rowsRaw = dualKey(raw, "rows");
    const rows = Array.isArray(rowsRaw)
      ? rowsRaw
          .map((r) => fieldRowSchema.safeParse(r))
          .filter((p) => p.success)
          .map((p) => p.data)
      : [];
    return {
      card_kind: "code_applicability_explain__lookup",
      ...base,
      code: typeof code === "string" ? code : "",
      building_label: typeof building_label === "string" ? building_label : "",
      summary: typeof summary === "string" ? summary : "",
      rows,
    };
  });

const formulaVariableSchema = z.unknown().transform((raw, ctx) => {
  if (!isObject(raw)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "expected object" });
    return z.NEVER;
  }
  const name = dualKey(raw, "name");
  const value = dualKey(raw, "value");
  const unit = dualKey(raw, "unit");
  const citation = dualKey(raw, "citation");
  const citationParsed = nullableCitationRef.safeParse(citation);
  return {
    name: typeof name === "string" ? name : "",
    value: typeof value === "string" ? value : "",
    unit: typeof unit === "string" ? unit : null,
    citation: citationParsed.success ? citationParsed.data : null,
  };
});

const computedSchema = z
  .unknown()
  .transform((raw, ctx): CodeApplicabilityExplainComputed => {
    if (!isObject(raw)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "expected object" });
      return z.NEVER;
    }
    const base = readBaseFields(raw);
    const code = dualKey(raw, "code");
    const building_label = dualKey(raw, "building_label");
    const summary = dualKey(raw, "summary");
    const formulaRaw = dualKey(raw, "formula");
    const formula = isObject(formulaRaw)
      ? {
          template:
            typeof dualKey(formulaRaw, "template") === "string"
              ? (dualKey(formulaRaw, "template") as string)
              : "",
          variables: Array.isArray(dualKey(formulaRaw, "variables"))
            ? (dualKey(formulaRaw, "variables") as unknown[])
                .map((v) => formulaVariableSchema.safeParse(v))
                .filter((p) => p.success)
                .map((p) => p.data)
            : [],
        }
      : { template: "", variables: [] };
    const result_label = dualKey(raw, "result_label");
    const result_value = dualKey(raw, "result_value");
    const compliance_period = dualKey(raw, "compliance_period");
    const data_cy = dualKey(raw, "data_cy");
    const filing_year = dualKey(raw, "filing_year");
    return {
      card_kind: "code_applicability_explain__computed",
      ...base,
      code: typeof code === "string" ? code : "",
      building_label: typeof building_label === "string" ? building_label : "",
      summary: typeof summary === "string" ? summary : "",
      formula,
      result_label: typeof result_label === "string" ? result_label : "",
      result_value: typeof result_value === "string" ? result_value : "",
      compliance_period:
        typeof compliance_period === "string" ? compliance_period : null,
      data_cy: typeof data_cy === "string" ? data_cy : null,
      filing_year: typeof filing_year === "string" ? filing_year : null,
    };
  });

const inspectionPhotoSchema = z.unknown().transform((raw, ctx) => {
  if (!isObject(raw)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "expected object" });
    return z.NEVER;
  }
  const caption = dualKey(raw, "caption");
  const href = dualKey(raw, "href");
  const s3_key = dualKey(raw, "s3_key");
  return {
    caption: typeof caption === "string" ? caption : "",
    href: typeof href === "string" ? href : null,
    s3_key: typeof s3_key === "string" ? s3_key : null,
  };
});

const visualSchema = z
  .unknown()
  .transform((raw, ctx): CodeApplicabilityExplainVisual => {
    if (!isObject(raw)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "expected object" });
      return z.NEVER;
    }
    const base = readBaseFields(raw);
    const code = dualKey(raw, "code");
    const building_label = dualKey(raw, "building_label");
    const summary = dualKey(raw, "summary");
    const findingsRaw = dualKey(raw, "inspector_findings");
    const inspector_findings = Array.isArray(findingsRaw)
      ? findingsRaw
          .map((r) => fieldRowSchema.safeParse(r))
          .filter((p) => p.success)
          .map((p) => p.data)
      : [];
    const photosRaw = dualKey(raw, "photos");
    const photos = Array.isArray(photosRaw)
      ? photosRaw
          .map((p) => inspectionPhotoSchema.safeParse(p))
          .filter((p) => p.success)
          .map((p) => p.data)
      : undefined;
    return {
      card_kind: "code_applicability_explain__visual",
      ...base,
      code: typeof code === "string" ? code : "",
      building_label: typeof building_label === "string" ? building_label : "",
      summary: typeof summary === "string" ? summary : "",
      inspector_findings,
      photos,
    };
  });

const VIOLATION_STATUSES = new Set([
  "OPEN",
  "STAYED",
  "PAID",
  "DISMISSED",
  "HEARING_SCHEDULED",
  "DEFAULTED",
  "IN_CURE",
]);

const violationRowSchema = z.unknown().transform((raw, ctx) => {
  if (!isObject(raw)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "expected object" });
    return z.NEVER;
  }
  const violation_id = dualKey(raw, "violation_id");
  const issued_on = dualKey(raw, "issued_on");
  const description = dualKey(raw, "description");
  const penalty_usd = dualKey(raw, "penalty_usd");
  const status = dualKey(raw, "status");
  const why = dualKey(raw, "why");
  const whyParsed = nullableCitationRef.safeParse(why);
  return {
    violation_id: typeof violation_id === "string" ? violation_id : "",
    issued_on: typeof issued_on === "string" ? issued_on : "",
    description: typeof description === "string" ? description : "",
    penalty_usd: typeof penalty_usd === "number" ? penalty_usd : 0,
    status:
      typeof status === "string" && VIOLATION_STATUSES.has(status)
        ? (status as
            | "OPEN"
            | "STAYED"
            | "PAID"
            | "DISMISSED"
            | "HEARING_SCHEDULED"
            | "DEFAULTED"
            | "IN_CURE")
        : "OPEN",
    why: whyParsed.success ? whyParsed.data : null,
  };
});

const listViolationsSchema = z
  .unknown()
  .transform((raw, ctx): ListActiveViolations => {
    if (!isObject(raw)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "expected object" });
      return z.NEVER;
    }
    const base = readBaseFields(raw);
    const building_label = dualKey(raw, "building_label");
    const agency = dualKey(raw, "agency");
    const rowsRaw = dualKey(raw, "rows");
    const rows = Array.isArray(rowsRaw)
      ? rowsRaw
          .map((r) => violationRowSchema.safeParse(r))
          .filter((p) => p.success)
          .map((p) => p.data)
      : [];
    return {
      card_kind: "list_active_violations",
      ...base,
      building_label: typeof building_label === "string" ? building_label : "",
      agency: typeof agency === "string" ? agency : "",
      rows,
    };
  });

const penaltyBucketSchema = z.unknown().transform((raw, ctx) => {
  if (!isObject(raw)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "expected object" });
    return z.NEVER;
  }
  const label = dualKey(raw, "label");
  const amount_usd = dualKey(raw, "amount_usd");
  const share_pct_raw = dualKey(raw, "share_pct");
  if (typeof share_pct_raw !== "number") {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "share_pct must be a number (0–100)",
    });
    return z.NEVER;
  }
  return {
    label: typeof label === "string" ? label : "",
    amount_usd: typeof amount_usd === "number" ? amount_usd : 0,
    share_pct: percentSchema.parse(share_pct_raw),
  };
});

const penaltyByDeadlineSchema = z.unknown().transform((raw, ctx) => {
  if (!isObject(raw)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "expected object" });
    return z.NEVER;
  }
  const deadline = dualKey(raw, "deadline");
  const code = dualKey(raw, "code");
  const amount_usd = dualKey(raw, "amount_usd");
  return {
    deadline: typeof deadline === "string" ? deadline : "",
    code: typeof code === "string" ? code : "",
    amount_usd: typeof amount_usd === "number" ? amount_usd : 0,
  };
});

const penaltyByForumSchema = z.unknown().transform((raw, ctx) => {
  if (!isObject(raw)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "expected object" });
    return z.NEVER;
  }
  const forum = dualKey(raw, "forum");
  const amount_usd = dualKey(raw, "amount_usd");
  const share_pct_raw = dualKey(raw, "share_pct");
  const detail = dualKey(raw, "detail");
  if (typeof share_pct_raw !== "number") {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "share_pct must be a number (0–100)",
    });
    return z.NEVER;
  }
  return {
    forum: typeof forum === "string" ? forum : "",
    amount_usd: typeof amount_usd === "number" ? amount_usd : 0,
    share_pct: percentSchema.parse(share_pct_raw),
    detail: typeof detail === "string" ? detail : null,
  };
});

const penaltyBreakdownSchema = z
  .unknown()
  .transform((raw, ctx): PortfolioPenaltyTotalBreakdown => {
    if (!isObject(raw)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "expected object" });
      return z.NEVER;
    }
    const base = readBaseFields(raw);
    const total_usd = dualKey(raw, "total_usd");
    const byLawRaw = dualKey(raw, "by_law");
    const byDeadlineRaw = dualKey(raw, "by_deadline");
    const byForumRaw = dualKey(raw, "by_forum");
    const by_law = Array.isArray(byLawRaw)
      ? byLawRaw
          .map((r) => penaltyBucketSchema.safeParse(r))
          .filter((p) => p.success)
          .map((p) => p.data)
      : [];
    const by_deadline = Array.isArray(byDeadlineRaw)
      ? byDeadlineRaw
          .map((r) => penaltyByDeadlineSchema.safeParse(r))
          .filter((p) => p.success)
          .map((p) => p.data)
      : [];
    const by_forum = Array.isArray(byForumRaw)
      ? byForumRaw
          .map((r) => penaltyByForumSchema.safeParse(r))
          .filter((p) => p.success)
          .map((p) => p.data)
      : [];
    return {
      card_kind: "portfolio_penalty_total_breakdown",
      ...base,
      total_usd: typeof total_usd === "number" ? total_usd : 0,
      by_law,
      by_deadline,
      by_forum,
    };
  });

const startFilingSchema = z.unknown().transform((raw, ctx): StartFilingFor => {
  if (!isObject(raw)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "expected object" });
    return z.NEVER;
  }
  const base = readBaseFields(raw);
  const action_id = dualKey(raw, "action_id");
  const building_label = dualKey(raw, "building_label");
  const bin = dualKey(raw, "bin");
  const code = dualKey(raw, "code");
  const description = dualKey(raw, "description");
  const compliance_period = dualKey(raw, "compliance_period");
  const data_cy = dualKey(raw, "data_cy");
  const filing_year = dualKey(raw, "filing_year");
  const primary_label = dualKey(raw, "primary_label");
  const cancel_label = dualKey(raw, "cancel_label");
  return {
    card_kind: "start_filing_for",
    ...base,
    action_id: typeof action_id === "string" ? action_id : "",
    building_label: typeof building_label === "string" ? building_label : "",
    bin: typeof bin === "string" ? bin : null,
    code: typeof code === "string" ? code : "",
    description: typeof description === "string" ? description : "",
    compliance_period:
      typeof compliance_period === "string" ? compliance_period : null,
    data_cy: typeof data_cy === "string" ? data_cy : null,
    filing_year: typeof filing_year === "string" ? filing_year : null,
    primary_label:
      typeof primary_label === "string" ? primary_label : undefined,
    cancel_label: typeof cancel_label === "string" ? cancel_label : undefined,
  };
});

const ownerQuestionRowSchema = z.unknown().transform((raw, ctx) => {
  if (!isObject(raw)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "expected object" });
    return z.NEVER;
  }
  const building_label = dualKey(raw, "building_label");
  const code = dualKey(raw, "code");
  const question = dualKey(raw, "question");
  const answer_options = dualKey(raw, "answer_options");
  const sub_article = dualKey(raw, "sub_article");
  return {
    building_label: typeof building_label === "string" ? building_label : "",
    code: typeof code === "string" ? code : "",
    question: typeof question === "string" ? question : "",
    answer_options: Array.isArray(answer_options)
      ? answer_options.filter((s): s is string => typeof s === "string")
      : [],
    sub_article: typeof sub_article === "string" ? sub_article : null,
  };
});

const ownerQuestionsSchema = z
  .unknown()
  .transform((raw, ctx): ListPendingOwnerQuestions => {
    if (!isObject(raw)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "expected object" });
      return z.NEVER;
    }
    const base = readBaseFields(raw);
    const headline = dualKey(raw, "headline");
    const rowsRaw = dualKey(raw, "rows");
    const rows = Array.isArray(rowsRaw)
      ? rowsRaw
          .map((r) => ownerQuestionRowSchema.safeParse(r))
          .filter((p) => p.success)
          .map((p) => p.data)
      : [];
    return {
      card_kind: "list_pending_owner_questions",
      ...base,
      headline: typeof headline === "string" ? headline : "",
      rows,
    };
  });

const sourceCitationFieldSchema = z.unknown().transform((raw, ctx) => {
  if (!isObject(raw)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "expected object" });
    return z.NEVER;
  }
  const key = dualKey(raw, "key");
  const value = dualKey(raw, "value");
  return {
    key: typeof key === "string" ? key : "",
    value: typeof value === "string" ? value : "",
  };
});

const sourceCitationSchema = z
  .unknown()
  .transform((raw, ctx): SourceCitation => {
    if (!isObject(raw)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "expected object" });
      return z.NEVER;
    }
    const base = readBaseFields(raw);
    const title = dualKey(raw, "title");
    const fieldsRaw = dualKey(raw, "fields");
    const fields = Array.isArray(fieldsRaw)
      ? fieldsRaw
          .map((f) => sourceCitationFieldSchema.safeParse(f))
          .filter((p) => p.success)
          .map((p) => p.data)
      : [];
    const dataset_url = dualKey(raw, "dataset_url");
    const refresh_note = dualKey(raw, "refresh_note");
    return {
      card_kind: "source_citation",
      ...base,
      title: typeof title === "string" ? title : "",
      fields,
      dataset_url: typeof dataset_url === "string" ? dataset_url : null,
      refresh_note: typeof refresh_note === "string" ? refresh_note : null,
    };
  });

const pendingClassificationSchema = z
  .unknown()
  .transform((raw, ctx): PendingClassification => {
    if (!isObject(raw)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "expected object" });
      return z.NEVER;
    }
    const base = readBaseFields(raw);
    const building_label = dualKey(raw, "building_label");
    const bbl = dualKey(raw, "bbl");
    const bin = dualKey(raw, "bin");
    const code = dualKey(raw, "code");
    const reason = dualKey(raw, "reason");
    const classifier_version = dualKey(raw, "classifier_version");
    const contest_url = dualKey(raw, "contest_url");
    return {
      card_kind: "pending_classification",
      ...base,
      building_label: typeof building_label === "string" ? building_label : "",
      bbl: typeof bbl === "string" ? bbl : "",
      bin: typeof bin === "string" ? bin : "",
      code: typeof code === "string" ? code : "",
      reason: typeof reason === "string" ? reason : "",
      classifier_version:
        typeof classifier_version === "string" ? classifier_version : "",
      contest_url: typeof contest_url === "string" ? contest_url : null,
    };
  });

const FILING_STATUSES = new Set([
  "draft",
  "owner_signed",
  "rdp_signed",
  "submitted",
  "accepted",
  "rejected",
  "withdrawn",
  "awaiting_payment",
  "extension_granted",
  "audit_commented",
]);

const filingStatusSchema = z.unknown().transform((raw, ctx): FilingStatus => {
  if (!isObject(raw)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: "expected object" });
    return z.NEVER;
  }
  const base = readBaseFields(raw);
  const building_label = dualKey(raw, "building_label");
  const code = dualKey(raw, "code");
  const status = dualKey(raw, "status");
  const compliance_period = dualKey(raw, "compliance_period");
  const data_cy = dualKey(raw, "data_cy");
  const filing_year = dualKey(raw, "filing_year");
  const submitted_at = dualKey(raw, "submitted_at");
  const dob_confirmation = dualKey(raw, "dob_confirmation");
  const rejection_reason = dualKey(raw, "rejection_reason");
  if (typeof status !== "string" || !FILING_STATUSES.has(status)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `filing_status.status must be one of the FilingStatusBadge enum, got "${String(status)}"`,
    });
    return z.NEVER;
  }
  return {
    card_kind: "filing_status",
    ...base,
    building_label: typeof building_label === "string" ? building_label : "",
    code: typeof code === "string" ? code : "",
    status: status as FilingStatus["status"],
    compliance_period:
      typeof compliance_period === "string" ? compliance_period : null,
    data_cy: typeof data_cy === "string" ? data_cy : null,
    filing_year: typeof filing_year === "string" ? filing_year : null,
    submitted_at: typeof submitted_at === "string" ? submitted_at : null,
    dob_confirmation:
      typeof dob_confirmation === "string" ? dob_confirmation : null,
    rejection_reason:
      typeof rejection_reason === "string" ? rejection_reason : null,
  };
});

// ---------------------------------------------------------------------
// Top-level discriminator-driven parser
// ---------------------------------------------------------------------

// ---------------------------------------------------------------------
// Phase 2 K — `mitigation_lever`
// ---------------------------------------------------------------------

const mitigationLeverRowSchema = z
  .unknown()
  .transform((raw, ctx): MitigationLeverRow => {
    if (!isObject(raw)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "expected object" });
      return z.NEVER;
    }
    const label = dualKey(raw, "label");
    const status = dualKey(raw, "status");
    if (typeof label !== "string" || typeof status !== "string") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "label + status required",
      });
      return z.NEVER;
    }
    const savings = dualKey(raw, "estimated_savings_usd");
    const why = dualKey(raw, "why");
    const note = dualKey(raw, "note");
    const simulate_tool = dualKey(raw, "simulate_tool");
    const whyParsed = nullableCitationRef.safeParse(why);
    return {
      label,
      status: status as MitigationLeverRow["status"],
      estimated_savings_usd:
        typeof savings === "number" ? savings : null,
      why: whyParsed.success ? whyParsed.data : null,
      note: typeof note === "string" ? note : null,
      simulate_tool: typeof simulate_tool === "string" ? simulate_tool : null,
    };
  });

const mitigationLeverSchema = z
  .unknown()
  .transform((raw, ctx): MitigationLever => {
    if (!isObject(raw)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "expected object" });
      return z.NEVER;
    }
    const base = readBaseFields(raw);
    const building_label = dualKey(raw, "building_label");
    const exceedance_tons = dualKey(raw, "exceedance_tons");
    const under_limit = dualKey(raw, "under_limit");
    const rows = dualKey(raw, "rows");
    const preface = dualKey(raw, "preface");
    const disclaimer = dualKey(raw, "disclaimer");
    if (
      typeof building_label !== "string" ||
      typeof disclaimer !== "string" ||
      !Array.isArray(rows)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "mitigation_lever missing required fields",
      });
      return z.NEVER;
    }
    const parsedRows: MitigationLeverRow[] = [];
    for (const r of rows) {
      const result = mitigationLeverRowSchema.safeParse(r);
      if (result.success) parsedRows.push(result.data);
    }
    return {
      card_kind: "mitigation_lever",
      source: base.source,
      as_of: base.as_of,
      classification_version: base.classification_version,
      expandable: base.expandable,
      building_label,
      exceedance_tons:
        typeof exceedance_tons === "number" ? exceedance_tons : 0,
      under_limit: under_limit === true,
      rows: parsedRows,
      preface: typeof preface === "string" ? preface : null,
      disclaimer,
    };
  });

// ---------------------------------------------------------------------
// Phase 2 L — `simulate_retrofit`
// ---------------------------------------------------------------------

const bandedFloatSchema = z
  .unknown()
  .transform((raw, ctx): BandedFloat => {
    if (!isObject(raw)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "expected object" });
      return z.NEVER;
    }
    const label = dualKey(raw, "label");
    const low = dualKey(raw, "low");
    const central = dualKey(raw, "central");
    const high = dualKey(raw, "high");
    if (
      typeof label !== "string" ||
      typeof low !== "string" ||
      typeof central !== "string" ||
      typeof high !== "string"
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "band missing label/low/central/high",
      });
      return z.NEVER;
    }
    const unit = dualKey(raw, "unit");
    const citation = dualKey(raw, "citation");
    const citParsed = nullableCitationRef.safeParse(citation);
    return {
      label,
      low,
      central,
      high,
      unit: typeof unit === "string" ? unit : null,
      citation: citParsed.success ? citParsed.data : null,
    };
  });

const nullableBandedFloat = z.unknown().transform((raw, ctx) => {
  if (raw === undefined || raw === null) return null;
  const parsed = bandedFloatSchema.safeParse(raw);
  if (!parsed.success) {
    parsed.error.issues.forEach((i) => ctx.addIssue(i));
    return null;
  }
  return parsed.data;
});

const simulateRetrofitRowSchema = z
  .unknown()
  .transform((raw, ctx): SimulateRetrofitRow => {
    if (!isObject(raw)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "expected object" });
      return z.NEVER;
    }
    const label = dualKey(raw, "label");
    const today_value = dualKey(raw, "today_value");
    const with_change_value = dualKey(raw, "with_change_value");
    if (
      typeof label !== "string" ||
      typeof today_value !== "string" ||
      typeof with_change_value !== "string"
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "simulate row missing label/today/with_change",
      });
      return z.NEVER;
    }
    const delta_label = dualKey(raw, "delta_label");
    const why = dualKey(raw, "why");
    const whyParsed = nullableCitationRef.safeParse(why);
    return {
      label,
      today_value,
      with_change_value,
      delta_label: typeof delta_label === "string" ? delta_label : null,
      why: whyParsed.success ? whyParsed.data : null,
    };
  });

const simulateRetrofitSchema = z
  .unknown()
  .transform((raw, ctx): SimulateRetrofit => {
    if (!isObject(raw)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "expected object" });
      return z.NEVER;
    }
    const base = readBaseFields(raw);
    const intervention_key = dualKey(raw, "intervention_key");
    const intervention_label = dualKey(raw, "intervention_label");
    const building_label = dualKey(raw, "building_label");
    const summary = dualKey(raw, "summary");
    const disclaimer = dualKey(raw, "disclaimer");
    if (
      typeof intervention_key !== "string" ||
      typeof intervention_label !== "string" ||
      typeof building_label !== "string" ||
      typeof summary !== "string" ||
      typeof disclaimer !== "string"
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "simulate_retrofit missing required fields",
      });
      return z.NEVER;
    }
    const comparison_rows_raw = dualKey(raw, "comparison_rows");
    const comparison_rows: SimulateRetrofitRow[] = [];
    if (Array.isArray(comparison_rows_raw)) {
      for (const r of comparison_rows_raw) {
        const result = simulateRetrofitRowSchema.safeParse(r);
        if (result.success) comparison_rows.push(result.data);
      }
    }
    const cost_band = nullableBandedFloat.safeParse(dualKey(raw, "cost_band"));
    const payback_band = nullableBandedFloat.safeParse(
      dualKey(raw, "payback_band"),
    );
    const emissions_delta_band = nullableBandedFloat.safeParse(
      dualKey(raw, "emissions_delta_band"),
    );
    const penalty_savings_band = nullableBandedFloat.safeParse(
      dualKey(raw, "penalty_savings_band"),
    );
    const rent_regulated_disclaimer = dualKey(raw, "rent_regulated_disclaimer");
    const missing_data_kind = dualKey(raw, "missing_data_kind");
    const missing_data_cta_label = dualKey(raw, "missing_data_cta_label");
    const missing_data_cta_href = dualKey(raw, "missing_data_cta_href");
    const formula = dualKey(raw, "formula");
    return {
      card_kind: "simulate_retrofit",
      source: base.source,
      as_of: base.as_of,
      classification_version: base.classification_version,
      expandable: base.expandable,
      intervention_key:
        intervention_key as SimulateRetrofit["intervention_key"],
      intervention_label,
      building_label,
      summary,
      disclaimer,
      rent_regulated_disclaimer:
        typeof rent_regulated_disclaimer === "string"
          ? rent_regulated_disclaimer
          : null,
      comparison_rows,
      cost_band: cost_band.success ? cost_band.data : null,
      payback_band: payback_band.success ? payback_band.data : null,
      emissions_delta_band: emissions_delta_band.success
        ? emissions_delta_band.data
        : null,
      penalty_savings_band: penalty_savings_band.success
        ? penalty_savings_band.data
        : null,
      formula: formula as SimulateRetrofit["formula"] | undefined,
      missing_data_kind:
        missing_data_kind === "existing_no_benchmarking" ||
        missing_data_kind === "new_construction_no_history"
          ? missing_data_kind
          : null,
      missing_data_cta_label:
        typeof missing_data_cta_label === "string"
          ? missing_data_cta_label
          : null,
      missing_data_cta_href:
        typeof missing_data_cta_href === "string"
          ? missing_data_cta_href
          : null,
    };
  });

type SchemaParser = (
  raw: unknown,
) => z.SafeParseReturnType<unknown, ToolCardPayload>;

// Each entry takes a raw payload and returns the standard Zod
// SafeParseReturnType. Wrapping each variant schema's `.safeParse`
// in a thin lambda lets us erase the `ZodEffects<...>` type parameters
// that don't unify across the discriminated union.
const KIND_SCHEMAS: Record<CardKind, SchemaParser> = {
  list_buildings_eligible_for_code: (raw) =>
    listBuildingsSchema.safeParse(raw) as z.SafeParseReturnType<
      unknown,
      ToolCardPayload
    >,
  portfolio_compliance_summary: (raw) =>
    portfolioSummarySchema.safeParse(raw) as z.SafeParseReturnType<
      unknown,
      ToolCardPayload
    >,
  code_applicability_explain__lookup: (raw) =>
    lookupSchema.safeParse(raw) as z.SafeParseReturnType<
      unknown,
      ToolCardPayload
    >,
  code_applicability_explain__computed: (raw) =>
    computedSchema.safeParse(raw) as z.SafeParseReturnType<
      unknown,
      ToolCardPayload
    >,
  code_applicability_explain__visual: (raw) =>
    visualSchema.safeParse(raw) as z.SafeParseReturnType<
      unknown,
      ToolCardPayload
    >,
  list_active_violations: (raw) =>
    listViolationsSchema.safeParse(raw) as z.SafeParseReturnType<
      unknown,
      ToolCardPayload
    >,
  portfolio_penalty_total_breakdown: (raw) =>
    penaltyBreakdownSchema.safeParse(raw) as z.SafeParseReturnType<
      unknown,
      ToolCardPayload
    >,
  start_filing_for: (raw) =>
    startFilingSchema.safeParse(raw) as z.SafeParseReturnType<
      unknown,
      ToolCardPayload
    >,
  list_pending_owner_questions: (raw) =>
    ownerQuestionsSchema.safeParse(raw) as z.SafeParseReturnType<
      unknown,
      ToolCardPayload
    >,
  source_citation: (raw) =>
    sourceCitationSchema.safeParse(raw) as z.SafeParseReturnType<
      unknown,
      ToolCardPayload
    >,
  pending_classification: (raw) =>
    pendingClassificationSchema.safeParse(raw) as z.SafeParseReturnType<
      unknown,
      ToolCardPayload
    >,
  filing_status: (raw) =>
    filingStatusSchema.safeParse(raw) as z.SafeParseReturnType<
      unknown,
      ToolCardPayload
    >,
  mitigation_lever: (raw) =>
    mitigationLeverSchema.safeParse(raw) as z.SafeParseReturnType<
      unknown,
      ToolCardPayload
    >,
  simulate_retrofit: (raw) =>
    simulateRetrofitSchema.safeParse(raw) as z.SafeParseReturnType<
      unknown,
      ToolCardPayload
    >,
};

/**
 * Resolve the discriminator from either snake_case (`card_kind`) or
 * camelCase (`cardKind`) — the boundary cases we shipped Phase 1
 * patches for.
 */
const readCardKind = (raw: unknown): string | null => {
  if (!isObject(raw)) return null;
  const snake = raw.card_kind;
  if (typeof snake === "string") return snake;
  const camel = raw.cardKind;
  if (typeof camel === "string") return camel;
  return null;
};

/**
 * Parse a raw payload (either snake_case or camelCase) into a typed
 * `ToolCardPayload`. Returns `null` if the discriminator is missing
 * or the schema fails — callers (e.g. `ToolCardDispatcher`) log + skip.
 *
 * IMPORTANT: this is the SINGLE consumer-boundary normalizer for
 * orchestrator card payloads. Don't write a second one; delete the
 * `humps.decamelizeKeys` call in `AssistantMessageBody.tsx` if you
 * see it and route through here instead.
 */
export const parseToolCard = (raw: unknown): ToolCardPayload | null => {
  const kind = readCardKind(raw);
  if (!kind) return null;
  const parser = KIND_SCHEMAS[kind as CardKind];
  if (!parser) return null;
  const result = parser(raw);
  if (!result.success) {
    if (typeof import.meta !== "undefined" && import.meta.env?.DEV) {
      // eslint-disable-next-line no-console
      console.warn(
        `[tool-cards/schemas] parseToolCard("${kind}") failed:`,
        result.error.issues,
      );
    }
    return null;
  }
  return result.data;
};

/**
 * Test helper — exposed only via the named export so the lint rule
 * can keep `card_kind` access scoped to schema/replay surfaces.
 */
export const testReadCardKind = readCardKind;
